// pages/support/sms/SmsPage.tsx

import { useState, useMemo, useEffect } from "react";
import { Search, Filter, X, Send, Calendar, Eye, Trash2, RefreshCw, MoreVertical } from "lucide-react";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import PageListLayout from "../../../layouts/PageListLayout";
import useSms from "../../../hooks/features/sms/useSms";
import SmsList from "../../../components/features/sms/SmsList";
import { useNavigate } from "react-router-dom";
import useAuth from "../../../hooks/features/auth/useAuth";

const PERIODS = [
  { value: "today", label: "Aujourd'hui" },
  { value: "yesterday", label: "Hier" },
  { value: "week", label: "Cette semaine" },
  { value: "month", label: "Ce mois-ci" },
  { value: "custom", label: "Personnalisé" },
];

const STATUS_OPTIONS = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-700" },
  sent: { label: "Envoyé", color: "bg-blue-100 text-blue-700" },
  delivered: { label: "Délivré", color: "bg-green-100 text-green-700" },
  failed: { label: "Échoué", color: "bg-red-100 text-red-700" },
};

export default function SmsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { smsList, loading, pagination, getSmsList, resendSms, deleteSms, deleteMultipleSms } = useSms();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("today");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [smsToDelete, setSmsToDelete] = useState<any>(null);
  const [selectedSms, setSelectedSms] = useState<any>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  console.log("sms", smsList);

  useEffect(() => {
    getSmsList(currentPage);
  }, [currentPage]);

  const filterByPeriod = (sms: any) => {
    const date = new Date(sms.created_at);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const startOfMonth = new Date(today);
    startOfMonth.setDate(1);

    switch (selectedPeriod) {
      case "today":
        return date >= today;
      case "yesterday":
        return date >= yesterday && date < today;
      case "week":
        return date >= startOfWeek;
      case "month":
        return date >= startOfMonth;
      case "custom": {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        if (customStartDate && customEndDate) {
          return date >= start && date <= end;
        }
        return true;
      }
      default:
        return true;
    }
  };

  const filteredSms = useMemo(() => {
    let result = smsList;
    result = result.filter(filterByPeriod);

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          s.message.toLowerCase().includes(searchLower) || s.recipients?.some((r: any) => r.to.includes(searchLower))
      );
    }

    if (selectedStatus) {
      result = result.filter((s) => {
        const statusCount = s.recipients?.filter((r: any) => r.status === selectedStatus).length || 0;
        return statusCount > 0;
      });
    }

    return result;
  }, [smsList, searchTerm, selectedStatus, selectedPeriod, customStartDate, customEndDate]);

  const stats = {
    total: smsList.length,
    sent: smsList.reduce((acc, s) => acc + (s.recipients?.filter((r: any) => r.status === "sent").length || 0), 0),
    delivered: smsList.reduce(
      (acc, s) => acc + (s.recipients?.filter((r: any) => r.status === "delivered").length || 0),
      0
    ),
    failed: smsList.reduce((acc, s) => acc + (s.recipients?.filter((r: any) => r.status === "failed").length || 0), 0),
    pending: smsList.reduce(
      (acc, s) => acc + (s.recipients?.filter((r: any) => r.status === "pending").length || 0),
      0
    ),
  };

  const handleResend = async (smsId: string, recipientId: string) => {
    const result = await resendSms(smsId, recipientId);
    if (result) {
      alertSuccess("SMS renvoyé avec succès");
      getSmsList(currentPage);
    }
  };

  const handleDelete = async () => {
    if (!smsToDelete) return;
    setIsDeleting(true);
    const success = await deleteSms(smsToDelete.id);
    setIsDeleting(false);
    if (success) {
      setSmsToDelete(null);
      alertSuccess("SMS supprimé avec succès");
      getSmsList(currentPage);
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    const success = await deleteMultipleSms(itemsToDelete);
    if (success) {
      setItemsToDelete(null);
      setSelectedIds([]);
      alertSuccess(`${itemsToDelete.length} SMS supprimés`);
      getSmsList(currentPage);
    } else {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedStatus("");
    setSearchTerm("");
    setSelectedPeriod("today");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const hasActiveFilters = selectedStatus !== "" || searchTerm.trim() !== "";

  const getActionsMenu = (sms: any): Menu[] => {
    return [
      {
        label: "Voir les détails",
        icon: Eye,
        onClick: () => setSelectedSms(sms),
      },
      {
        label: "Rafraîchir le statut",
        icon: RefreshCw,
        onClick: () => {
          // Rafraîchir le statut
          alertSuccess("Statut rafraîchi");
        },
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => setSmsToDelete(sms),
      },
    ];
  };

  return (
    <PageListLayout title="">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">SMS</h1>
              <p className="text-sm text-gray-500 mt-1">Envoyez et gérez vos SMS</p>
            </div>
            <button
              onClick={() => navigate(`/${user?.role}/sms/new`)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
            >
              <Send size={18} />
              Nouveau SMS
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3">
              <p className="text-xs text-gray-700/70">Total</p>
              <p className="text-xl font-bold text-gray-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3">
              <p className="text-xs text-blue-700/70">Envoyés</p>
              <p className="text-xl font-bold text-blue-700">{stats.sent}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3">
              <p className="text-xs text-green-700/70">Délivrés</p>
              <p className="text-xl font-bold text-green-700">{stats.delivered}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-3">
              <p className="text-xs text-yellow-700/70">En attente</p>
              <p className="text-xl font-bold text-yellow-700">{stats.pending}</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-3">
              <p className="text-xs text-red-700/70">Échoués</p>
              <p className="text-xl font-bold text-red-700">{stats.failed}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Période :</span>
            </div>
            <div className="flex gap-1 flex-wrap">
              {PERIODS.map((period) => (
                <button
                  key={period.value}
                  onClick={() => {
                    setSelectedPeriod(period.value);
                    if (period.value !== "custom") {
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }
                  }}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    selectedPeriod === period.value
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>

            {selectedPeriod === "custom" && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-gray-500">à</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
          </div>

          <div className="relative mt-4">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par message ou numéro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Tous</option>
                      {Object.entries(STATUS_OPTIONS).map(([key, { label }]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredSms.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun SMS trouvé pour cette période</p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <span className="text-sm text-gray-500">{filteredSms.length} SMS affiché(s)</span>
            </div>
            <SmsList
              smsList={filteredSms}
              onAction={(sms) => setSelectedSms(sms)}
              onResend={handleResend}
              onSelectSms={(selected) => setSelectedIds(selected.map((s) => s.id))}
              selectable={true}
              selectActions={[
                {
                  label: "Supprimer",
                  onClick: () => setItemsToDelete(selectedIds),
                  className: "bg-red-600 text-white",
                },
              ]}
            />
          </>
        )}

        {pagination.total > 0 && (
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={() => {
                const page = currentPage - 1;
                setCurrentPage(page);
                getSmsList(page);
              }}
              disabled={currentPage === 1}
              className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Précédent
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} sur {pagination.lastPage} ({pagination.total} SMS)
            </span>
            <button
              onClick={() => {
                const page = currentPage + 1;
                setCurrentPage(page);
                getSmsList(page);
              }}
              disabled={currentPage === pagination.lastPage}
              className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {selectedSms && (
        <MenuModal
          title={`SMS - ${new Date(selectedSms.created_at).toLocaleDateString("fr-FR")}`}
          isOpen={true}
          onClose={() => setSelectedSms(null)}
          icon={null}
          menu={getActionsMenu(selectedSms)}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!smsToDelete}
        onClose={() => setSmsToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le SMS"
        message={`Supprimer ce SMS ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} SMS ?`}
      />
    </PageListLayout>
  );
}
